// import React from 'react'

// const Analytics = () => {
//   return (
//     <div>Analytics will be shown here</div>
//   )
// }

// export default Analytics

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import SingleLineChart from "../../components/charts/Analytics/SingleLineChart";
import LineAxisChart from "../../components/charts/Analytics/LineAxisChart";
import SingleBarChart from "../../components/charts/Analytics/SingleBarChart";
import DualLineChart from "../../components/charts/Analytics/DualLineChart";
import StackedBarChart from "../../components/charts/Analytics/StackedBarChart";
import SecondBarChart from "../../components/charts/Analytics/SecondBarChart";
import HeatmapChart from "../../components/charts/Analytics/HeatmapChart";
import LoanTenure from "../../components/charts/Analytics/LoanTenure";
import ShareLineChart from "../../components/charts/Analytics/SharesLineChart";
import DividendBarChart from "../../components/charts/Analytics/DivBarChart";
import TargetLinesChart from "../../components/charts/Analytics/TargetLinesChart";
import FinancialBarChart from "../../components/charts/Analytics/FinancialBarChart";
import OperationalLineChart from "../../components/charts/Analytics/OperationalLineChart";
import NPLineChart from "../../components/charts/Analytics/NPLineChart";
import CashreserveBarChart from "../../components/charts/Analytics/CashReserveBarChart";
import convertDateFormat from "../../hooks/convertDate";
import Calendar from "../../components/calendar/Calendar";
import {
  fetchMemberReportAnalytics,
  fetchMemberGrowthRate,
  fetchSavingsDepositsAnalytics,
  fetchSharesIssuedAnalytics,
  fetchLoanSummaryAnalytics,
  fetchLoanDisbursementsAnalytics,
  fetchLoanDefaultRateAnalytics,
  fetchAverageLoanTenureAnalytics,
  fetchNplRatioAnalytics,
} from "../../sdk/reportsAndanalytics/reportsAndAnalytics";

const Analytics = () => {
  const [memberReportAnalytics, setMemberReportAnalytics] = useState(null);
  const [memberGrowthRate, setMemberGrowthRate] = useState(null);
  const [savingsDeposits, setSavingsDeposits] = useState(null);
  const [sharesIssued, setSharesIssued] = useState(null);
  const [loanSummary, setLoanSummary] = useState(null);
  const [loanDisbursements, setLoanDisbursements] = useState(null);
  const [loanDefaultRate, setLoanDefaultRate] = useState(null);
  const [averageLoanTenure, setAverageLoanTenure] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [finalDate, setFinalDate] = useState(null);
  const [startDay, setStartDay] = useState(null);
  const [finishDay, setFinishDay] = useState(null);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [dateBegin, setDateBegin] = useState(null);
  const [dateClose, setDateClose] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [uiFocus, setUiFocus] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [currentInput, setCurrentInput] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [activeInput, setActiveInput] = useState(null);
  const [focused, setFocused] = useState(null);
  const [customerFocusedInput, setCustomerFocusedInput] = useState(null);
  const [focusedDateInput, setFocusedDateInput] = useState(null);

  const [loanType, setLoanType] = useState("");
  const [selectedLoan, setSelectedLoan] = useState("");
  const [loanChoice, setLoanChoice] = useState("");
  const [nplRatio, setNplRatio] = useState(null);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const getNplRatioAnalytics = async () => {
    try {
      const data = await fetchNplRatioAnalytics(selectedYear);
      setNplRatio(data);
    } catch (err) {
      console.error("Fetch NPL ratio analytics error:", err);
    }
  };

  useEffect(() => {
    getNplRatioAnalytics();
  }, [selectedYear]);

  const getMemberReportAnalytics = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (fromDate && toDate) {
        params.from = convertDateFormat(fromDate);
        params.to = convertDateFormat(toDate);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }
      const data = await fetchMemberReportAnalytics(params);
      setMemberReportAnalytics(data);
    } catch (err) {
      console?.error("Fetch member report analytics error:", err);
    }
  };

  const getMemberGrowthRate = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (startDate && endDate) {
        params.from = convertDateFormat(startDate);
        params.to = convertDateFormat(endDate);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }

      const data = await fetchMemberGrowthRate(params);
      setMemberGrowthRate(data);
    } catch (err) {
      console?.error("Fetch member growth rate error:", err);
    }
  };

  const getSavingsDepositsAnalytics = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (initialDate && finalDate) {
        params.from = convertDateFormat(initialDate);
        params.to = convertDateFormat(finalDate);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }

      const data = await fetchSavingsDepositsAnalytics(params);
      setSavingsDeposits(data);
    } catch (err) {
      console.error("Fetch savings deposits analytics error:", err);
    }
  };

  const getSharesIssuedAnalytics = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (startDay && finishDay) {
        params.from = convertDateFormat(startDay);
        params.to = convertDateFormat(finishDay);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }

      const data = await fetchSharesIssuedAnalytics(params);
      setSharesIssued(data);
    } catch (err) {
      console?.error("Fetch shares issued analytics error:", err);
    }
  };

  const getLoanDisbursementsAnalytics = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (periodStart && periodEnd) {
        params.from = convertDateFormat(periodStart);
        params.to = convertDateFormat(periodEnd);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }

      if (selectedLoan) {
        params.loanType = selectedLoan;
      }

      const data = await fetchLoanDisbursementsAnalytics(params);
      setLoanDisbursements(data);
    } catch (err) {
      console?.error("Fetch loan disbursements analytics error:", err);
    }
  };

  useEffect(() => {
    getMemberReportAnalytics();
    getMemberGrowthRate();
    getSavingsDepositsAnalytics();
    getSharesIssuedAnalytics();
  }, [
    fromDate,
    toDate,
    startDate,
    endDate,
    initialDate,
    finalDate,
    startDay,
    finishDay,
  ]);

  const getLoanSummaryAnalytics = async () => {
    try {
      const params = {};

      // If both dates are selected → use them
      if (rangeStart && rangeEnd) {
        params.from = convertDateFormat(rangeStart);
        params.to = convertDateFormat(rangeEnd);
      } else {
        params.months = 12;
      }

      // Add loanType if selected
      if (loanType) {
        params.loanType = loanType;
      }

      const data = await fetchLoanSummaryAnalytics(params);
      setLoanSummary(data);
    } catch (err) {
      console.error("Fetch loan summary analytics error:", err);
    }
  };

  const getLoanDefaultRateAnalytics = async () => {
    try {
      const params = {};

      // Use selected dates if available
      if (dateBegin && dateClose) {
        params.from = convertDateFormat(dateBegin);
        params.to = convertDateFormat(dateClose);
      } else {
        // Otherwise fallback to default months
        params.months = 12;
      }

      if (loanChoice) {
        params.loanType = loanChoice;
      }

      const data = await fetchLoanDefaultRateAnalytics(params);
      setLoanDefaultRate(data);
    } catch (err) {
      console.error("Fetch loan default rate analytics error:", err);
    }
  };

  const getAverageLoanTenureAnalytics = async () => {
    try {
      const params = {};

      if (minDate && maxDate) {
        params.from = convertDateFormat(minDate);
        params.to = convertDateFormat(maxDate);
      } else {
        params.months = 12;
      }

      const data = await fetchAverageLoanTenureAnalytics(params);
      setAverageLoanTenure(data);
    } catch (err) {
      console.error("Fetch average loan tenure analytics error:", err);
    }
  };

  useEffect(() => {
    getLoanSummaryAnalytics();
    getAverageLoanTenureAnalytics();
  }, [rangeStart, rangeEnd, minDate, maxDate, loanType]);
  useEffect(() => {
    getLoanDisbursementsAnalytics();
    getLoanDefaultRateAnalytics();
  }, [periodStart, periodEnd, dateBegin, dateClose, selectedLoan, loanChoice]);
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Sojrel || Analytics</title>
      </Helmet>
      <div className="w-full bg-[#F5F6F7] min-h-screen overflow-y-auto">
        <div className="bg-[#F5F6F7] px-4 sm:px-4 md:px-6 lg:px-4 mb-32">
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center text-[#005C53] text-lg sm:text-xl md:text-2xl font-bold">
              Analytics
            </div>
          </div>

          <div className="mt-4">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Total Members & Growth Rate
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-10 gap-3">
            {/* Left Card */}
            <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="flex justify-between mt-5">
                <div className="text-[16px] text-[#005C53] font-[700] px-6">
                  Total Members
                </div>
                <Calendar
                  startDate={fromDate}
                  endDate={toDate}
                  setStartDate={setFromDate}
                  setEndDate={setToDate}
                  focusedInput={customerFocusedInput}
                  setFocusedInput={setCustomerFocusedInput}
                />
              </div>
              <div className="flex flex-col sm:flex-row mt-5">
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Total Members
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {memberReportAnalytics?.totals?.totalMembers}
                  </div>
                </div>
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Active
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {memberReportAnalytics?.totals?.activeMembers}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <SingleLineChart data={memberReportAnalytics?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            <div className="lg:w-[29%] w-full flex flex-col gap-3">
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  New members this month
                </p>
                <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                  {memberReportAnalytics?.newMembers?.inPeriod}
                </p>
              </div>
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  New members last year
                </p>
                <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                  {memberReportAnalytics?.newMembers?.lastYear}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-5 gap-3">
            {/* Left Card */}
            <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="flex justify-between mt-5">
                <div className="text-[16px] text-[#005C53] font-[700] px-6">
                  Members Growth Rate
                </div>
                <Calendar
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  focusedInput={focusedDateInput}
                  setFocusedInput={setFocusedDateInput}
                />
              </div>
              <div className="flex flex-col sm:flex-row mt-5">
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    This month
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {memberGrowthRate?.growthRate?.thisMonth?.value}%
                  </div>
                </div>
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    This year
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {memberGrowthRate?.growthRate?.thisYear?.value}%
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <LineAxisChart data={memberGrowthRate?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            <div className="lg:w-[29%] w-full flex flex-col gap-3">
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col justify-center">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  Members growth rate this year (%)
                </p>
                <p className="text-[#06B217] text-[32px] font-normal py-2 sm:py-5">
                  {memberGrowthRate?.growthRate?.thisYear?.value}%
                </p>
              </div>
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col justify-center">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  Members growth rate last year (%)
                </p>
                <p className="text-[#FF5757] text-[32px] font-normal py-2 sm:py-5">
                  {memberGrowthRate?.growthRate?.lastYear?.value || 0.0}%
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Savings Deposits
              {/* Savings Deposits & Net Change */}
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-3 gap-3">
            {/* Left Card */}
            <div className="lg:w-[100%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="flex justify-between mt-5">
                <div className="text-[16px] text-[#005C53] font-[700] px-6">
                  Total Savings Deposits
                </div>
                <Calendar
                  startDate={initialDate}
                  endDate={finalDate}
                  setStartDate={setInitialDate}
                  setEndDate={setFinalDate}
                  focusedInput={focused}
                  setFocusedInput={setFocused}
                />
              </div>
              <div className="flex flex-col sm:flex-row mt-5">
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    This month
                  </div>
                  <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                    KES &nbsp;
                    {Number(
                      savingsDeposits?.totals?.thisMonth || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                <div className="mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Total this year
                  </div>
                  <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                    KES &nbsp;
                    {Number(
                      savingsDeposits?.totals?.thisYear || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                <div className="px-5 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Total last year
                  </div>
                  <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                    KES &nbsp;
                    {Number(
                      savingsDeposits?.totals?.lastYear || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <SingleBarChart data={savingsDeposits?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            {/* <div className="lg:w-[50%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 text-[16px] text-[#005C53] font-[700] px-6">
                Withdrawals vs Deposits (Net Change)
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="flex flex-col sm:flex-row">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total withdrawals
                      <br />
                      this year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      KES 90,909.53
                    </div>
                  </div>
                  <div className="px-2 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total withdrawals this
                      <br />
                      month
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      KES 13,909.53
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mr-6">
                  <div className="flex-col flex justify-end gap-2 items-start">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="w-5 h-[1px] bg-[#005C53] block"></span>
                        <span className="-ml-[1px] w-2 h-2 border border-[#005C53] rounded-full block"></span>
                      </div>
                      <span className="text-[12px] text-[#737373] font-[400]">
                        Deposits
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="w-5 h-[1px] bg-[#FF5757] block"></span>
                        <span className="-ml-[1px] w-2 h-2 border border-[#FF5757] rounded-full block"></span>
                      </div>
                      <span className="text-[12px] text-[#737373] font-[400]">
                        Withdrawals
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="">
                <DualLineChart />
              </div>
            </div> */}
          </div>

          <div className="mt-10">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Loan Performance
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-5 gap-3">
            {/* Left Card */}
            <div className="lg:w-[58%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="mt-3 flex items-center justify-between px-3">
                <div className=" text-[16px] text-[#005C53] font-[700]">
                  Loan Summary
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  value={loanType}
                  onChange={(e) => setLoanType(e?.target?.value)}
                >
                  <option value="">-- Select Loan Type</option>
                  <option value="Business_loan">Business Loan</option>
                  <option value="Checkoff_loan">Check-off Loan</option>
                  <option value="Development_loan">Development Loan</option>
                  <option value="Emergency_loan">Emergency Loan</option>
                  <option value="Flash_loan">Flash Loan</option>
                  <option value="Guarantor_loan">Normal Loan</option>
                </select>
              </div>
              <div className="flex flex-col sm:flex-row mt-3">
                <div className="px-5 mt-2 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Period
                  </div>
                  <Calendar
                    startDate={rangeStart}
                    endDate={rangeEnd}
                    setStartDate={setRangeStart}
                    setEndDate={setRangeEnd}
                    focusedInput={activeField}
                    setFocusedInput={setActiveField}
                  />
                </div>
              </div>
              <div className="mt-3 px-3 mr-4">
                <StackedBarChart data={loanSummary?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            <div className="lg:w-[42%] w-full flex flex-col rounded-[20px] gap-5">
              <div className="flex gap-3">
                <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[273px] px-3">
                  <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                    Principal Due
                  </p>
                  <p className="text-[#000000] text-[30px] font-normal py-2 sm:py-5">
                    KES &nbsp;
                    {Number(
                      loanSummary?.cards?.principalDue || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[273px] px-3">
                  <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                    Fees Due
                  </p>
                  <p className="text-[#000000] text-[30px] font-normal py-2 sm:py-5">
                    KES &nbsp;
                    {Number(
                      loanSummary?.cards?.feesDue || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[273px] px-3">
                  <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                    Interest Due
                  </p>
                  <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                    KES &nbsp;
                    {Number(
                      loanSummary?.cards?.interestDue || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[273px] px-3">
                  <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                    Total Due
                  </p>
                  <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                    KES &nbsp;
                    {Number(
                      loanSummary?.cards?.totalDue || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-10 gap-3">
            {/* Left Card */}
            <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="mt-2 flex items-center justify-between px-3">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Total Loans Disbursed
                </div>
                <div className="flex gap-3">
                  <select
                    className="border border-[#D5D5D5] px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    value={selectedLoan}
                    onChange={(e) => setSelectedLoan(e?.target?.value)}
                  >
                    <option value="">--Select Loan Type</option>
                    <option value="Business_loan">Business Loan</option>
                    <option value="Checkoff_loan">Check-off Loan</option>
                    <option value="Development_loan">Development Loan</option>
                    <option value="Emergency_loan">Emergency Loan</option>
                    <option value="Flash_loan">Flash Loan</option>
                    <option value="Guarantor_loan">Normal Loan</option>
                  </select>

                  <Calendar
                    startDate={periodStart}
                    endDate={periodEnd}
                    setStartDate={setPeriodStart}
                    setEndDate={setPeriodEnd}
                    focusedInput={currentInput}
                    setFocusedInput={setCurrentInput}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row mt-3">
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    This month
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {loanDisbursements?.cards?.countThisMonth}
                  </div>
                </div>
                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Total this year
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    {loanDisbursements?.cards?.countThisYear}
                  </div>
                </div>

                <div className="px-6 mt-3 sm:mt-0">
                  <div className="text-[12px] text-[#737373] font-[400]">
                    Total amount this year
                  </div>
                  <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                    KES &nbsp;
                    {Number(
                      loanDisbursements?.cards?.amountThisYear || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-2 px-3">
                <SecondBarChart data={loanDisbursements?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            <div className="lg:w-[29%] w-full flex flex-col gap-3">
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  Total disbursed this month
                </p>
                <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                  KES &nbsp;
                  {Number(
                    loanDisbursements?.cards?.amountThisMonth || "0.00",
                  )?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                <p className="text-[#000000] text-[15px] font-normal py-2 sm:py-5">
                  Total disbursed last year
                </p>
                <p className="text-[#000000] text-[32px] font-normal py-2 sm:py-5">
                  KES &nbsp;
                  {Number(
                    loanDisbursements?.cards?.amountLastYear || "0.00",
                  )?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-3 gap-3">
            <div className="lg:w-[50%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="mt-5 flex items-center justify-between px-3">
                <div className="text-[16px] text-[#005C53] font-[700]">
                  Loan Default Rate (%)
                </div>
                <Calendar
                  startDate={dateBegin}
                  endDate={dateClose}
                  setStartDate={setDateBegin}
                  setEndDate={setDateClose}
                  focusedInput={selectedField}
                  setFocusedInput={setSelectedField}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col sm:flex-row mt-5">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      This month(%)
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      {loanDefaultRate?.cards?.defaultRateThisMonthPct}%
                    </div>
                  </div>
                  <div className="mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total this year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      {loanDefaultRate?.cards?.defaultRateThisYearPct}%
                    </div>
                  </div>
                  <div className="px-5 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Last year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      {loanDefaultRate?.cards?.defaultRateLastYearPct}%
                    </div>
                  </div>
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md py-1 px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  value={loanChoice}
                  onChange={(e) => setLoanChoice(e?.target?.value)}
                >
                  <option value="">--Select Loan Type</option>
                  <option value="Business_loan">Business Loan</option>
                  <option value="Checkoff_loan">Check-off Loan</option>
                  <option value="Development_loan">Development Loan</option>
                  <option value="Emergency_loan">Emergency Loan</option>
                  <option value="Flash_loan">Flash Loan</option>
                  <option value="Guarantor_loan">Normal Loan</option>
                </select>
              </div>
              <div className="h-[350px] overflow-y-auto">
                <HeatmapChart rawData={loanDefaultRate?.heatmap} />
              </div>
            </div>

            <div className="lg:w-[50%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[16px] text-[#005C53] font-[700] px-6">
                  Average Loan Tenure
                </div>
                <Calendar
                  startDate={minDate}
                  endDate={maxDate}
                  setStartDate={setMinDate}
                  setEndDate={setMaxDate}
                  focusedInput={uiFocus}
                  setFocusedInput={setUiFocus}
                />
              </div>
              <div className="mt-12">
                <LoanTenure apiData={averageLoanTenure?.chart} />
              </div>
            </div>
          </div>

          <div className="mt-10">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Shares
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-3 gap-3">
            {/* Left Card */}
            <div className="lg:w-[100%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
              <div className="mt-5 flex items-center justify-between px-3">
                <div className="text-[16px] text-[#005C53] font-[700]">
                  Total Shares Issued
                </div>
                <Calendar
                  startDate={startDay}
                  endDate={finishDay}
                  setStartDate={setStartDay}
                  setEndDate={setFinishDay}
                  focusedInput={activeInput}
                  setFocusedInput={setActiveInput}
                />
              </div>
              <div className="flex justify-end items-center gap-2 px-3 mt-2">
                <div className="flex items-center">
                  <span className="w-5 h-[1px] bg-[#FF5757] block"></span>
                  <span className="-ml-[1px] w-2 h-2 border border-[#FF5757] rounded-full block"></span>
                </div>
                <span className="text-[12px] text-[#737373] font-[400]">
                  Shares issued
                </span>
              </div>

              <div className="mt-2 mb-5">
                <ShareLineChart data={sharesIssued?.chart} />
              </div>
            </div>

            {/* Right Cards */}
            {/* <div className="lg:w-[50%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-5">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Total Dividends Paid
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  defaultValue=""
                >
                  <option value="">--Select Year</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div className="mt-2 mb-5">
                <DividendBarChart />
              </div>
            </div> */}
          </div>

          {/* <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
            <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Dividend Payout Ratio (%)
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  defaultValue=""
                >
                  <option value="">--Select Year</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="flex flex-col sm:flex-row">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Target
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      40.0%
                    </div>
                  </div>
                  <div className="px-2 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Last Year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      38.5%
                    </div>
                  </div>
                  <div className="px-2 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Average this year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      29.9%
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mr-6">
                  <div className="flex-col flex justify-end gap-2 items-start">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="w-5 h-[1px] bg-[#005C53] block"></span>
                        <span className="-ml-[1px] w-2 h-2 border border-[#005C53] rounded-full block"></span>
                      </div>
                      <span className="text-[12px] text-[#737373] font-[400]">
                        Dividend payout
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="w-5 h-[1px] block bg-[repeating-linear-gradient(to_right,#FF5757_0,#FF5757_2px,transparent_2px,transparent_4px)]"></span>
                        <span className="-ml-[1px] w-2 h-2 border border-[#FF5757] rounded-full block"></span>
                      </div>
                      <span className="text-[12px] text-[#737373] font-[400]">
                        Target
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-5">
                <TargetLinesChart />
              </div>
            </div>
          </div> */}

          {/* <div className="mt-10">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Financial Health
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div> */}

          {/* <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
            <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Total Revenue / Total Expenses / Net Profit
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  defaultValue=""
                >
                  <option value="">--Select Year</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div className="mt-5 mb-5 ml-6">
                <FinancialBarChart />
              </div>
            </div>
          </div> */}

          {/* <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
            <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Operational Efficiency Ratio
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  defaultValue=""
                >
                  <option value="">--Select Year</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div className="mt-5 mb-5 ml-10">
                <OperationalLineChart />
              </div>
            </div>
          </div> */}

          <div className="mt-10">
            <span className="text-xs sm:text-sm md:text-base font-medium text-black">
              Liquidity & Risk
            </span>
            <div
              className=""
              style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
            ></div>
          </div>

          {/* <div className="flex flex-col lg:flex-row justify-between mt-12 gap-3">
            <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Cash Reserve Ratio (%)
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  defaultValue=""
                >
                  <option value="">--Select Year</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div className="mt-5 mb-5 px-10">
                <CashreserveBarChart />
              </div>
            </div>
          </div> */}

          <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
            <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
              <div className="mt-5 flex items-center justify-between px-6">
                <div className="text-[15px] text-[#005C53] font-[700]">
                  Non-Performing Loans (NPL) Ratio (%)
                </div>
                <select
                  className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  <option value="">--Select Year</option>
                  <option value="2020">2020</option>
                  <option value="2021">2021</option>
                  <option value="2022">2022</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              </div>

              <div className="mt-5 mb-5 ml-10">
                <NPLineChart data={nplRatio?.chart} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Analytics;
